import type { QualificationRule } from './types';
export interface QualificationContext {
  seasonPointsRank: number;
  careerRanking: number;
  priorSeasonPointsRank: number;
  currentSeasonWins: number;
  careerWins: number;
  topTenCount: number;
  priorEventFinish: number;
  defendingChampion: boolean;
  majorWins: number;
  currentSeasonWinEventName?: string;
}
export interface QualificationResult {
  eligible: boolean;
  reason: string;
  qualifyingProgress: number;
  qualifyingThreshold: number;
  closestCondition?: string;
  conditions?: string[];
}
export class QualificationEngine {
  evaluate(rule: QualificationRule, c: QualificationContext): QualificationResult {
    if (rule.type === 'any') {
      const results = (rule.rules ?? []).map((r) => this.evaluate(r, c));
      const win = results.find((r) => r.eligible);
      if (win)
        return { ...win, conditions: results.map((r) => r.reason), closestCondition: win.reason };
      const distance = (result: QualificationResult) =>
        result.reason.includes('rank')
          ? Math.max(0, result.qualifyingProgress - result.qualifyingThreshold) /
            result.qualifyingThreshold
          : Math.max(0, result.qualifyingThreshold - result.qualifyingProgress) /
            result.qualifyingThreshold;
      const closest = [...results].sort((a, b) => distance(a) - distance(b))[0];
      return {
        eligible: false,
        reason: results.map((r) => r.reason).join(' or '),
        qualifyingProgress: closest?.qualifyingProgress ?? 0,
        qualifyingThreshold: closest?.qualifyingThreshold ?? 1,
        closestCondition: closest?.reason,
        conditions: results.map((r) => r.reason),
      };
    }
    const t = rule.threshold ?? 1;
    const rank = (v: number, label: string) => ({
      eligible: v <= t,
      reason: v <= t ? `Qualified via ${label}` : `Requires ${label} of ${t} or better`,
      qualifyingProgress: v,
      qualifyingThreshold: t,
    });
    switch (rule.type) {
      case 'open':
        return {
          eligible: true,
          reason: 'Open event',
          qualifyingProgress: 1,
          qualifyingThreshold: 1,
        };
      case 'seasonPointsRank':
      case 'seasonChampionshipField':
        return rank(c.seasonPointsRank, 'season-points rank');
      case 'careerRanking':
        return rank(c.careerRanking, 'career ranking');
      case 'priorSeasonPointsRank':
        return rank(c.priorSeasonPointsRank, 'prior-season rank');
      case 'currentSeasonWin':
        return {
          eligible: c.currentSeasonWins > 0,
          reason: c.currentSeasonWins
            ? `Qualified through victory${c.currentSeasonWinEventName ? ` at ${c.currentSeasonWinEventName}` : ' this season'}`
            : 'Requires a current-season win',
          qualifyingProgress: c.currentSeasonWins,
          qualifyingThreshold: 1,
        };
      case 'careerWin':
        return {
          eligible: c.careerWins > 0,
          reason: c.careerWins ? 'Qualified by a career win' : 'Requires a career win',
          qualifyingProgress: c.careerWins,
          qualifyingThreshold: 1,
        };
      case 'topTenCount':
        return {
          eligible: c.topTenCount >= t,
          reason:
            c.topTenCount >= t ? 'Qualified by top-10 finishes' : `Requires ${t} top-10 finishes`,
          qualifyingProgress: c.topTenCount,
          qualifyingThreshold: t,
        };
      case 'priorEventFinish':
        return rank(c.priorEventFinish, 'prior-event finish');
      case 'defendingChampion':
        return {
          eligible: c.defendingChampion,
          reason: c.defendingChampion ? 'Defending champion' : 'Must be defending champion',
          qualifyingProgress: +c.defendingChampion,
          qualifyingThreshold: 1,
        };
      case 'majorWinner':
        return {
          eligible: c.majorWins > 0,
          reason: c.majorWins ? 'Qualified as a major winner' : 'Requires a prior major win',
          qualifyingProgress: c.majorWins,
          qualifyingThreshold: 1,
        };
    }
  }
}
